import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatsPage = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('last_seen');
  const [sortAsc, setSortAsc] = useState(false);

  const fetchData = useCallback(async (pwd) => {
    try {
      const res = await fetch(`${API}/api/stats/visitors?password=${encodeURIComponent(pwd)}`);
      const json = await res.json();
      if (json.error) {
        setError('Wrong password');
        setAuthenticated(false);
        return;
      }
      setData(json);
      setAuthenticated(true);
      setError('');
    } catch {
      setError('Failed to fetch');
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    fetchData(password);
  };

  // Auto-refresh every 10s once authenticated
  useEffect(() => {
    if (!authenticated) return;
    const interval = setInterval(() => fetchData(password), 10000);
    return () => clearInterval(interval);
  }, [authenticated, password, fetchData]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString();
  };

  const truncateUA = (ua) => {
    if (!ua) return '—';
    if (ua.length > 60) return ua.slice(0, 60) + '...';
    return ua;
  };

  // Login screen
  if (!authenticated) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0f1a', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace'
      }}>
        <form onSubmit={handleLogin} style={{
          background: '#111827', border: '1px solid #1e293b', borderRadius: 12,
          padding: 40, width: 340, textAlign: 'center'
        }}>
          <h2 style={{ color: '#94a3b8', marginBottom: 24, fontSize: 18 }}>Stats Access</h2>
          <input
            data-testid="stats-password-input"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            autoFocus
            style={{
              width: '100%', padding: '10px 14px', background: '#0f172a',
              border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0',
              fontSize: 14, marginBottom: 16, outline: 'none', boxSizing: 'border-box'
            }}
          />
          {error && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</p>}
          <button
            data-testid="stats-login-btn"
            type="submit"
            style={{
              width: '100%', padding: '10px 0', background: '#3b82f6', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  // Sort and filter visitors
  let visitors = data?.visitors || [];
  if (search) {
    const q = search.toLowerCase();
    visitors = visitors.filter(v =>
      v.ip.includes(q) ||
      v.last_page.toLowerCase().includes(q) ||
      v.user_agent.toLowerCase().includes(q) ||
      v.pages_viewed.some(p => p.toLowerCase().includes(q))
    );
  }

  visitors = [...visitors].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey];
    if (sortKey === 'visit_count') {
      return sortAsc ? va - vb : vb - va;
    }
    va = String(va || ''); vb = String(vb || '');
    return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  const recentViews = data?.recent_views || [];

  const thStyle = {
    padding: '10px 14px', textAlign: 'left', color: '#94a3b8', fontSize: 12,
    fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
    borderBottom: '1px solid #1e293b', cursor: 'pointer', userSelect: 'none',
    whiteSpace: 'nowrap'
  };
  const tdStyle = {
    padding: '10px 14px', color: '#cbd5e1', fontSize: 13,
    borderBottom: '1px solid #111827', verticalAlign: 'top'
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0a0f1a', color: '#e2e8f0',
      fontFamily: 'monospace', padding: '24px 32px'
    }} data-testid="stats-dashboard">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9' }}>Visitor Stats</h1>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <span style={{ color: '#64748b', fontSize: 13 }}>
            {data?.total_unique_ips || 0} unique IPs &middot; {data?.total_visits || 0} total visits
          </span>
          <button
            data-testid="stats-refresh-btn"
            onClick={() => fetchData(password)}
            style={{
              padding: '6px 14px', background: '#1e293b', color: '#94a3b8',
              border: '1px solid #334155', borderRadius: 6, fontSize: 12, cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Search */}
      <input
        data-testid="stats-search-input"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by IP, page, or user agent..."
        style={{
          width: '100%', maxWidth: 400, padding: '8px 14px', background: '#111827',
          border: '1px solid #1e293b', borderRadius: 8, color: '#e2e8f0',
          fontSize: 13, marginBottom: 20, outline: 'none', boxSizing: 'border-box'
        }}
      />

      {/* Visitors Table */}
      <div style={{ overflowX: 'auto', marginBottom: 40 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#0f172a', borderRadius: 8 }}>
          <thead>
            <tr>
              <th style={thStyle} onClick={() => handleSort('ip')}>
                IP Address {sortKey === 'ip' ? (sortAsc ? '↑' : '↓') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('visit_count')}>
                Visits {sortKey === 'visit_count' ? (sortAsc ? '↑' : '↓') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('last_page')}>
                Last Page {sortKey === 'last_page' ? (sortAsc ? '↑' : '↓') : ''}
              </th>
              <th style={thStyle}>Pages Viewed</th>
              <th style={thStyle} onClick={() => handleSort('first_seen')}>
                First Seen {sortKey === 'first_seen' ? (sortAsc ? '↑' : '↓') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('last_seen')}>
                Last Seen {sortKey === 'last_seen' ? (sortAsc ? '↑' : '↓') : ''}
              </th>
              <th style={thStyle}>User Agent</th>
            </tr>
          </thead>
          <tbody>
            {visitors.length === 0 && (
              <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#475569' }}>No visitors yet</td></tr>
            )}
            {visitors.map((v, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}>
                <td style={{ ...tdStyle, fontWeight: 600, color: '#60a5fa' }}>{v.ip}</td>
                <td style={{ ...tdStyle, fontWeight: 700, color: '#22c55e' }}>{v.visit_count}</td>
                <td style={{ ...tdStyle, color: '#fbbf24' }}>{v.last_page || '—'}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {v.pages_viewed.map((p, j) => (
                      <span key={j} style={{
                        padding: '2px 8px', background: '#1e293b', borderRadius: 4,
                        fontSize: 11, color: '#94a3b8'
                      }}>{p}</span>
                    ))}
                  </div>
                </td>
                <td style={{ ...tdStyle, fontSize: 12, color: '#64748b' }}>{formatTime(v.first_seen)}</td>
                <td style={{ ...tdStyle, fontSize: 12, color: '#94a3b8' }}>{formatTime(v.last_seen)}</td>
                <td style={{ ...tdStyle, fontSize: 11, color: '#475569', maxWidth: 250 }}>{truncateUA(v.user_agent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent Page Views */}
      <h2 style={{ fontSize: 16, fontWeight: 600, color: '#94a3b8', marginBottom: 12 }}>
        Last {recentViews.length} Page Views
      </h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#0f172a', borderRadius: 8 }}>
          <thead>
            <tr>
              <th style={thStyle}>Time</th>
              <th style={thStyle}>IP</th>
              <th style={thStyle}>Path</th>
              <th style={thStyle}>User Agent</th>
            </tr>
          </thead>
          <tbody>
            {[...recentViews].reverse().map((v, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}>
                <td style={{ ...tdStyle, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{formatTime(v.timestamp)}</td>
                <td style={{ ...tdStyle, color: '#60a5fa' }}>{v.ip}</td>
                <td style={{ ...tdStyle, color: '#fbbf24' }}>{v.path}</td>
                <td style={{ ...tdStyle, fontSize: 11, color: '#475569', maxWidth: 250 }}>{truncateUA(v.user_agent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StatsPage;
